/**
 * MODULE REGISTRY — frontend mirror of `backend/config/modules.js`.
 *
 * A "module" is one product surface: a nav entry, the routes behind it, and the
 * API it talks to. A tenant's `enabledModules` is a list of these keys, and the
 * superadmin portal is where that list is edited.
 *
 * ⚠️  KEEP KEY-FOR-KEY IDENTICAL WITH THE BACKEND FILE. The duplication is
 * deliberate — a JS backend and a TS frontend share no package — and the two
 * files are the same table written twice. A key that exists here but not there
 * can never be enabled; a key that exists there but not here is invisible in
 * the UI. `npm run check:modules` compares them.
 *
 * This file is the authority for WHICH modules exist. It is NOT the authority
 * for whether a user may reach one: the backend guard
 * (`middlewares/moduleGuard.js`) is, and it answers 404 for anything switched
 * off. Everything here exists so the UI never offers a door that the API will
 * refuse to open.
 */

export type ModuleAudience = "system" | "driver";

export type ModuleGroup = "main" | "operations" | "management" | "driver";

export type ModuleKey =
  | "dashboard"
  | "shipments"
  | "shipmentGroups"
  | "containerOrders"
  | "works"
  | "orders"
  | "tasks"
  | "calendar"
  | "invoices"
  | "partners"
  | "addresses"
  | "vehicles"
  | "services"
  | "costServices"
  | "integrations"
  | "drivers"
  | "users"
  | "organisationSettings"
  | "helpCenter"
  | "driverContainers"
  | "driverWorks"
  | "driverShipments";

export interface ModuleDef {
  key: ModuleKey;
  /** Which portal the surface belongs to — the office, or the driver app. */
  audience: ModuleAudience;
  group: ModuleGroup;
  /** Cannot be switched off: without it an admin could not administer at all. */
  core?: boolean;
  /** Switching the parent off switches this off with it. */
  dependsOn?: ModuleKey;
}

export const MODULES: readonly ModuleDef[] = [
  { key: "dashboard", audience: "system", group: "main", core: true },

  { key: "shipments", audience: "system", group: "operations" },
  {
    key: "shipmentGroups",
    audience: "system",
    group: "operations",
    dependsOn: "shipments",
  },
  { key: "containerOrders", audience: "system", group: "operations" },
  { key: "works", audience: "system", group: "operations" },
  { key: "orders", audience: "system", group: "operations" },
  { key: "tasks", audience: "system", group: "operations" },
  { key: "calendar", audience: "system", group: "operations" },
  { key: "invoices", audience: "system", group: "operations" },

  { key: "partners", audience: "system", group: "management" },
  { key: "addresses", audience: "system", group: "management" },
  { key: "vehicles", audience: "system", group: "management" },
  { key: "services", audience: "system", group: "management" },
  { key: "costServices", audience: "system", group: "management" },
  { key: "integrations", audience: "system", group: "management" },
  // Presentation-level only, and the one module with no backend gate — see
  // the note in backend/config/modules.js. Disabling it hides the nav entry
  // and blocks the route; the underlying data is the tenant's own staff, which
  // they can already see under Users.
  { key: "drivers", audience: "system", group: "management" },
  { key: "users", audience: "system", group: "management", core: true },
  {
    key: "organisationSettings",
    audience: "system",
    group: "management",
    core: true,
  },
  { key: "helpCenter", audience: "system", group: "management", core: true },

  {
    key: "driverContainers",
    audience: "driver",
    group: "driver",
    dependsOn: "containerOrders",
  },
  { key: "driverWorks", audience: "driver", group: "driver", dependsOn: "works" },
  {
    key: "driverShipments",
    audience: "driver",
    group: "driver",
    dependsOn: "shipments",
  },
] as const;

export const MODULE_KEYS: readonly ModuleKey[] = MODULES.map((m) => m.key);

const BY_KEY = new Map<string, ModuleDef>(MODULES.map((m) => [m.key, m]));

export const getModule = (key: string): ModuleDef | undefined => BY_KEY.get(key);

export const isModuleKey = (key: string): key is ModuleKey => BY_KEY.has(key);

export const CORE_MODULE_KEYS: readonly ModuleKey[] = MODULES.filter(
  (m) => m.core,
).map((m) => m.key);

/**
 * i18n keys for the labels the superadmin portal shows next to each toggle.
 * The tenant-facing nav reuses `navigation:items.*`; these are the same strings,
 * named here so the admin UI does not have to know the nav's shape.
 */
export const MODULE_LABEL_KEYS: Record<ModuleKey, string> = {
  dashboard: "items.dashboard",
  shipments: "items.shipments",
  shipmentGroups: "items.shipmentsGroup",
  containerOrders: "items.containerOrders",
  works: "items.works",
  orders: "items.orders",
  tasks: "items.tasks",
  calendar: "items.operationsCalendar",
  invoices: "items.invoices",
  partners: "items.partners",
  addresses: "items.addresses",
  vehicles: "items.vehicles",
  services: "items.services",
  costServices: "items.costServices",
  integrations: "items.integrations",
  drivers: "items.drivers",
  users: "items.users",
  organisationSettings: "items.organisationSettings",
  helpCenter: "items.helpCentre",
  driverContainers: "items.containerOrders",
  driverWorks: "items.works",
  driverShipments: "items.shipments",
};

/**
 * Normalize an arbitrary list into a valid, self-consistent module set —
 * the same rules the backend applies before storing: unknown keys dropped,
 * core keys forced on, and any module whose parent is off dropped with it.
 *
 * Used by the superadmin editor so the toggles a user sees are exactly the set
 * that will come back from the server, with no surprise corrections on save.
 */
export const normalizeEnabledModules = (keys: readonly string[]): ModuleKey[] => {
  const requested = new Set<ModuleKey>(keys.filter(isModuleKey));

  for (const key of CORE_MODULE_KEYS) requested.add(key);

  // Fixed point: dropping a parent can orphan a child, which can orphan its
  // own child. The chain is bounded by the registry, so this terminates.
  let changed = true;
  while (changed) {
    changed = false;
    for (const key of [...requested]) {
      const parent = BY_KEY.get(key)?.dependsOn;
      if (parent && !requested.has(parent)) {
        requested.delete(key);
        changed = true;
      }
    }
  }

  // Registry order, so stored and compared arrays are stable.
  return MODULE_KEYS.filter((k) => requested.has(k));
};

/**
 * "Unknown" is treated as "everything on", matching the backend: a tenant
 * created before module management has no stored list, and rendering that as a
 * blank app would be worse than trusting the guard to say no.
 */
export const resolveEnabledModules = (
  stored: readonly string[] | null | undefined,
): ReadonlySet<ModuleKey> =>
  new Set(stored?.length ? stored.filter(isModuleKey) : MODULE_KEYS);
